import React, { useMemo } from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { InternalApiEntitySchemaField, InternalApiEntityType, InternalApiTransaction } from '../../api-internal';
import { getTransactionPath } from '../../navigation/helper';
import { faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InternalLink from '../../common/InternalLink';
import { WarnTooltip } from '../../common/tooltips/WarnTooltip';
import { getSchemaFieldsOfSearchResults, isSortable } from '../../search/helper';
import { useQueryEntitySchema } from '../../schema/entitySchemaActions';
import { SimpleValue } from './SimpleValue';
import { Counterparty } from './Counterparty';

export type BaseTransaction = Pick<InternalApiTransaction, 'id' | 'attributes'>;

interface UseTransactionColumnsProps<T extends BaseTransaction> {
  transactions: T[];
  enableSorting?: boolean;
}

interface TransactionColumns<T extends BaseTransaction> {
  columns: ColumnDef<T>[];
  isPending: boolean;
  isError: boolean;
}

export default function useTransactionColumns<T extends BaseTransaction>({
  transactions,
  enableSorting = true,
}: UseTransactionColumnsProps<T>): TransactionColumns<T> {
  const {
    data: schema,
    isPending: isSchemaPending,
    isError: isSchemaError,
  } = useQueryEntitySchema(InternalApiEntityType.TRANSACTION);

  const columns = useMemo(() => {
    if (!schema) return [];

    return getTransactionColumns<T>({
      transactions,
      schemaFields: schema.schemaFields,
      enableSorting,
    });
  }, [transactions, schema, enableSorting]);

  return {
    columns: columns as ColumnDef<T>[],
    isPending: isSchemaPending,
    isError: isSchemaError,
  };
}

interface TransactionColumnsOptions<T extends BaseTransaction> {
  transactions: T[];
  schemaFields: InternalApiEntitySchemaField[];
  enableSorting?: boolean;
}

function getTransactionColumns<T extends BaseTransaction>({
  transactions,
  schemaFields,
  enableSorting = true,
}: TransactionColumnsOptions<T>) {
  const columnHelper = createColumnHelper<T>();

  const transactionsPage = {
    content: transactions,
    total: transactions.length,
    number: 0,
    size: transactions.length,
  };
  const { schemaFieldsOfSearchResults, fieldNamesWithMissingSchema } = getSchemaFieldsOfSearchResults(
    transactionsPage,
    schemaFields
  );

  return [
    columnHelper.display({
      id: 'transactionLink',
      header: () =>
        fieldNamesWithMissingSchema.length ? (
          <WarnTooltip
            tooltip={`Fields ${fieldNamesWithMissingSchema} exist in Search results but are not specified in your Schema. You should stop seeing this warning when Transaction re-indexing has finished.`}
          />
        ) : undefined,
      cell: ({ row }) => {
        const transaction = row.original;
        return (
          <InternalLink
            title={<FontAwesomeIcon icon={faFileLines} style={{ fontSize: 16 }} />}
            link={getTransactionPath(transaction.id)}
            ariaLabel={'Link for Transaction ' + transaction.id}
            onClickOpenInNewTab={false}
          />
        );
      },
    }),
    columnHelper.display({
      id: 'counterparty',
      header: 'Counterparty',
      cell: ({ row }) => {
        const transaction = row.original;
        return <Counterparty attributes={transaction.attributes} />;
      },
    }),
    ...schemaFieldsOfSearchResults.map((schemaField) => {
      return columnHelper.accessor((row) => row.attributes, {
        id: schemaField.fieldName,
        header: schemaField.displayName,
        cell: ({ row }) => {
          const transaction = row.original;
          const attributes = transaction.attributes;
          return (
            <SimpleValue
              fieldName={schemaField.fieldName}
              fieldValue={attributes[schemaField.fieldName]}
              attributes={attributes}
              schemaField={schemaField}
            />
          );
        },
        enableSorting: enableSorting && isSortable(schemaField),
      });
    }),
    columnHelper.accessor((row) => row.id, {
      id: 'id',
      header: 'ID',
      cell: ({ getValue }) => getValue(),
      enableSorting: false,
    }),
  ];
}
